/**
 * Operator Dashboard State Store
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { apiFetch } from './api';

interface OperatorUser {
	id: string;
	operatorId: string;
	operatorName: string;
	email: string;
	role: 'owner' | 'admin' | 'viewer';
}

interface AuthState {
	isAuthenticated: boolean;
	user: OperatorUser | null;
	token: string | null;
	login: (email: string, password: string) => Promise<boolean>;
	logout: () => void;
}

export const useAuthStore = create<AuthState>()(
	persist(
		(set) => ({
			isAuthenticated: false,
			user: null,
			token: null,

			login: async (email: string, password: string) => {
				try {
					const response = await apiFetch('/api/v1/operator/auth/login', {
						method: 'POST',
						headers: { 'Content-Type': 'application/json' },
						body: JSON.stringify({ email, password }),
					});

					if (!response.ok) {
						return false;
					}

					const data = await response.json();
					set({
						isAuthenticated: true,
						user: data.user,
						token: data.token,
					});
					return true;
				} catch {
					return false;
				}
			},

			logout: () => {
				set({
					isAuthenticated: false,
					user: null,
					token: null,
				});
			},
		}),
		{
			name: 'operator-auth',
			partialize: (state) => ({
				isAuthenticated: state.isAuthenticated,
				user: state.user,
				token: state.token,
			}),
		}
	)
);

// Dashboard stats store
interface DashboardStats {
	totalGGR: string;
	totalBets: number;
	activePlayers: number;
	activeSessions: number;
	ggrToday: string;
	betsToday: number;
	recentBets: Array<{
		id: string;
		playerId: string;
		game: string;
		amount: string;
		won: boolean;
		createdAt: number;
	}>;
}

interface DashboardState {
	stats: DashboardStats | null;
	isLoading: boolean;
	error: string | null;
	fetchStats: () => Promise<void>;
}

export const useDashboardStore = create<DashboardState>((set) => ({
	stats: null,
	isLoading: false,
	error: null,

	fetchStats: async () => {
		const token = useAuthStore.getState().token;
		if (!token) return;

		set({ isLoading: true, error: null });

		try {
			const response = await apiFetch('/api/v1/operator/dashboard/stats', {
				headers: { Authorization: `Bearer ${token}` },
			});

			if (!response.ok) throw new Error('Failed to fetch stats');

			const data = await response.json();
			set({ stats: data, isLoading: false });
		} catch (err) {
			set({
				error: err instanceof Error ? err.message : 'Unknown error',
				isLoading: false,
			});
		}
	},
}));

// Players store
interface Player {
	id: string;
	externalId: string;
	currency: string;
	totalBets: number;
	totalWagered: string;
	totalWon: string;
	netResult: string;
	lastActive: number;
	createdAt: number;
}

interface PlayersState {
	players: Player[];
	selectedPlayer: Player | null;
	isLoading: boolean;
	error: string | null;
	fetchPlayers: () => Promise<void>;
	fetchPlayer: (id: string) => Promise<void>;
}

export const usePlayersStore = create<PlayersState>((set) => ({
	players: [],
	selectedPlayer: null,
	isLoading: false,
	error: null,

	fetchPlayers: async () => {
		const token = useAuthStore.getState().token;
		if (!token) return;

		set({ isLoading: true, error: null });

		try {
			const response = await apiFetch('/api/v1/operator/players', {
				headers: { Authorization: `Bearer ${token}` },
			});

			if (!response.ok) throw new Error('Failed to fetch players');

			const data = await response.json();
			set({ players: data.items, isLoading: false });
		} catch (err) {
			set({
				error: err instanceof Error ? err.message : 'Unknown error',
				isLoading: false,
			});
		}
	},

	fetchPlayer: async (id: string) => {
		const token = useAuthStore.getState().token;
		if (!token) return;

		set({ isLoading: true, error: null });

		try {
			const response = await apiFetch(`/api/v1/operator/players/${id}`, {
				headers: { Authorization: `Bearer ${token}` },
			});

			if (!response.ok) throw new Error('Failed to fetch player');

			const data = await response.json();
			set({ selectedPlayer: data, isLoading: false });
		} catch (err) {
			set({
				error: err instanceof Error ? err.message : 'Unknown error',
				isLoading: false,
			});
		}
	},
}));

// Bets store
interface Bet {
	id: string;
	playerId: string;
	game: string;
	amount: string;
	payout: string;
	won: boolean;
	multiplier: number;
	currency: string;
	createdAt: number;
}

interface BetsState {
	bets: Bet[];
	isLoading: boolean;
	error: string | null;
	fetchBets: (filters?: { game?: string; playerId?: string }) => Promise<void>;
}

export const useBetsStore = create<BetsState>((set) => ({
	bets: [],
	isLoading: false,
	error: null,

	fetchBets: async (filters) => {
		const token = useAuthStore.getState().token;
		if (!token) return;

		set({ isLoading: true, error: null });

		try {
			const params = new URLSearchParams();
			if (filters?.game) params.set('game', filters.game);
			if (filters?.playerId) params.set('playerId', filters.playerId);

			const response = await apiFetch(`/api/v1/operator/bets?${params}`, {
				headers: { Authorization: `Bearer ${token}` },
			});

			if (!response.ok) throw new Error('Failed to fetch bets');

			const data = await response.json();
			set({ bets: data.items, isLoading: false });
		} catch (err) {
			set({
				error: err instanceof Error ? err.message : 'Unknown error',
				isLoading: false,
			});
		}
	},
}));
